import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Settings, FileText, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import {
    getCompletedPolicies,
    getManagedPolicies,
    getPendingApprovalPolicies,
    deletePolicy,
    PolicyListItem,
    ManagedPolicyTab,
    ManagedPolicyCounts,
} from '../../services/api/policyService';
import LoadingRing from '../ui-components/LoadingRing';
import { useToast } from '../../hooks/use-toast';
import { PolicyFilters } from './PolicyFilters';
import { PolicyTable } from './PolicyTable';
import ConfirmActionDialog from './ConfirmActionDialog';
import { getDepartmentVariables } from '../../services/api/departmentVariablesService';
import AuthContext from '../../store/auth-context';
import PERMISSION_KEYS from '../../constants/permission-keys';
import RIGHTS_KEYS from '../../constants/rights-keys';
import { POLICY_SCHEMA_KEY } from './constants';
import { EVENT_KEYS, trackClick } from '../../services/api/trackingService';

const MANAGED_TAB_VALUES: ManagedPolicyTab[] = [
    'draft',
    'completed',
    'expired',
    'pending',
];

export default function DepartmentPoliciesPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { toast } = useToast();
    const context = useContext(AuthContext);
    const isAdmin = context.hasPermission(PERMISSION_KEYS.managePolicies);
    const canManageVariables =
        isAdmin && context.orRights([RIGHTS_KEYS.manageEmployees]);
    const canApprove = context.hasPermission(PERMISSION_KEYS.approvePolicy);

    const [policies, setPolicies] = useState<PolicyListItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState<ManagedPolicyTab>('completed');
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [isVariableSetupComplete, setIsVariableSetupComplete] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState<PolicyListItem | null>(null);
    const [counts, setCounts] = useState<ManagedPolicyCounts>({
        draft: 0,
        completed: 0,
        expired: 0,
        pending: 0,
    });

    const fetchPolicies = useCallback(async () => {
        setIsLoading(true);
        try {
            if (isAdmin) {
                const res = await getManagedPolicies({ tab: activeTab });
                setPolicies(res.data);
                setCounts(res.counts);
            } else if (canApprove) {
                const [completedRes, pendingRes] = await Promise.all([
                    getCompletedPolicies(),
                    getPendingApprovalPolicies(),
                ]);
                setPolicies(
                    activeTab === 'pending'
                        ? pendingRes.data
                        : completedRes.data
                );
                setCounts((prev) => ({
                    ...prev,
                    completed: completedRes.total,
                    pending: pendingRes.total,
                }));
            } else {
                const res = await getCompletedPolicies();
                setPolicies(res.data);
            }
        } catch (error) {
            console.error('Error fetching policies:', error);
            setPolicies([]);
        } finally {
            setIsLoading(false);
        }
    }, [activeTab, isAdmin, canApprove]);

    useEffect(() => {
        fetchPolicies();
    }, [fetchPolicies]);

    useEffect(() => {
        if (!isAdmin) return;
        getDepartmentVariables()
            .then((vars) => setIsVariableSetupComplete(vars.length > 0))
            .catch(() => setIsVariableSetupComplete(true));
    }, [isAdmin]);

    const allTags = useMemo(() => {
        const tagSet = new Set<string>();
        for (const p of policies) {
            for (const t of p.tags ?? []) tagSet.add(t);
        }
        return Array.from(tagSet).sort();
    }, [policies]);

    const filteredPolicies = useMemo(() => {
        let result = policies;

        if (selectedTags.length > 0) {
            result = result.filter((p) =>
                (p.tags ?? []).some((t) => selectedTags.includes(t))
            );
        }

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(
                (p) =>
                    p.title?.toLowerCase().includes(term) ||
                    (p.tags ?? []).some((t) => t.toLowerCase().includes(term)) ||
                    p.createdBy?.toLowerCase().includes(term)
            );
        }

        return result;
    }, [policies, selectedTags, searchTerm]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deletePolicy(deleteTarget._id);
            setPolicies((prev) => prev.filter((p) => p._id !== deleteTarget._id));
            if (isAdmin) {
                setCounts((prev) => ({
                    ...prev,
                    [activeTab]: Math.max(prev[activeTab] - 1, 0),
                }));
            }
            toast({ title: t('departmentPolicies.toasts.policyDeleted') });
        } catch {
            toast({
                title: t('departmentPolicies.toasts.failedToDelete'),
                variant: 'destructive',
            });
        }
        setDeleteTarget(null);
    };

    const emptyStateDescription = t('departmentPolicies.emptyState.description');

    return (
        <div className="container mx-auto max-w-6xl px-4 py-8">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold">{t('departmentPolicies.title')}</h1>
                    <p className="text-sm text-muted-foreground">
                        {isAdmin
                            ? t('departmentPolicies.subtitleAdmin')
                            : t('departmentPolicies.subtitleViewer')}
                    </p>
                </div>
                {isAdmin && (
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        {canManageVariables && (
                            <Button
                                variant="outline"
                                onClick={() =>
                                    navigate('/department-policies/settings')
                                }
                                className="w-full sm:w-auto"
                            >
                                <Settings className="h-4 w-4 mr-2" />
                                {t('departmentPolicies.settings')}
                            </Button>
                        )}
                        <Button
                            onClick={() => {
                                trackClick(EVENT_KEYS.clickCreatePolicy, {});
                                navigate(
                                    `/department-policies/new?schema=${POLICY_SCHEMA_KEY}`
                                );
                            }}
                            className="w-full sm:w-auto"
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            {t('departmentPolicies.createPolicy')}
                        </Button>
                    </div>
                )}
            </div>

            {canManageVariables && !isVariableSetupComplete ? (
                <div className="mb-6 rounded-lg border border-subtle bg-card p-4">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                            <p className="text-sm font-medium text-foreground">
                                {t('departmentPolicies.setupBanner.title')}
                            </p>
                            <p className="mt-1 text-sm text-muted-foreground">
                                {t('departmentPolicies.setupBanner.description')}
                            </p>
                        </div>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                trackClick(EVENT_KEYS.clickSetupVariables, {});
                                navigate('/department-policies/settings');
                            }}
                            className="w-full shrink-0 sm:w-auto"
                        >
                            {t('departmentPolicies.setupBanner.button')}
                        </Button>
                    </div>
                </div>
            ) : null}

            {isLoading ? (
                <div className="flex justify-center py-16">
                    <LoadingRing size="large" />
                </div>
            ) : (
                <>
                    <PolicyFilters
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        allTags={allTags}
                        selectedTags={selectedTags}
                        onTagsChange={setSelectedTags}
                    />

                    {(isAdmin || canApprove) && (
                        <Tabs
                            value={activeTab}
                            onValueChange={(value: string) =>
                                setActiveTab(value as ManagedPolicyTab)
                            }
                            className="mt-4"
                        >
                            <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
                                <TabsList className="inline-flex min-w-max">
                                {(isAdmin
                                    ? MANAGED_TAB_VALUES
                                    : ([
                                          'completed',
                                          'pending',
                                      ] as ManagedPolicyTab[])
                                ).map((tab) => (
                                    <TabsTrigger key={tab} value={tab}>
                                        <span>{t(`departmentPolicies.tabs.${tab}`)}</span>
                                        <span className={`ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-medium ${
                                            activeTab === tab && tab === 'expired'
                                                ? 'bg-red-500/15 text-red-400'
                                                : activeTab === tab && tab === 'pending'
                                                  ? 'bg-amber-500/20 text-amber-400'
                                                : activeTab === tab
                                                  ? 'bg-emerald-500/20 text-emerald-400'
                                                : 'bg-muted text-muted-foreground'
                                        }`}>
                                            {counts[tab] ?? 0}
                                        </span>
                                    </TabsTrigger>
                                ))}
                                </TabsList>
                            </div>
                        </Tabs>
                    )}

                    {filteredPolicies.length === 0 ? (
                        <div className="mt-4 flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
                            <FileText className="mb-3 h-10 w-10 text-muted-foreground" />
                            <h2 className="mb-1 text-lg font-semibold">
                                {t('departmentPolicies.emptyState.title')}
                            </h2>
                            <p className="max-w-md text-sm text-muted-foreground">
                                {emptyStateDescription}
                            </p>
                        </div>
                    ) : (
                        <div className="mt-4">
                            <PolicyTable
                                policies={filteredPolicies}
                                onDelete={isAdmin ? setDeleteTarget : undefined}
                                highlightExpiredDates={
                                    isAdmin && activeTab === 'expired'
                                }
                                showEffectiveDate={isAdmin}
                            />
                        </div>
                    )}
                </>
            )}

            <ConfirmActionDialog
                open={!!deleteTarget}
                onOpenChange={(open) => !open && setDeleteTarget(null)}
                title={t('departmentPolicies.deleteDialog.title')}
                description={t('departmentPolicies.deleteDialog.description', { title: deleteTarget?.title || t('departmentPolicies.editor.untitledPolicy') })}
                confirmLabel={t('departmentPolicies.deleteDialog.confirm')}
                onConfirm={handleDelete}
            />
        </div>
    );
}
